import { useRef, useLayoutEffect } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

/**
 * @callback callback
 */

/**
 * Runs a callback function whenever a specified element changes size.
 *
 * @param {object} targetRef - React ref of element we are monitoring.
 * @param {callback} callback - Runs when element changes size. Gets passed element's `contentRect` and the element itself.
 */
const useResizeOberserver = (targetRef, callback, enabled = true) => {
    const callbackRef = useRef(callback);

    useLayoutEffect(() => {
        callbackRef.current = callback;
    }, [callback]);

    // Observe on mount
    // Disconnect when unmounted
    // Note that ref must be ready when this effect runs and last until component is unmounted for this to work
    useLayoutEffect(() => {
        if (!enabled) {
            return undefined;
        }

        const target = targetRef.current;

        if (!target) {
            return undefined;
        }

        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];

            if (!entry) {
                return;
            }

            callbackRef.current(entry.contentRect, entry.target);
        });

        observer.observe(target);

        return () => observer.disconnect();
    }, [enabled, targetRef]);
};

export default useResizeOberserver;
